import React from 'react';
import { useTranslation } from 'react-i18next';

import { STRIPE_PROVIDER, type CheckoutProvider } from '@/utils/session-invoice';
import type { PaymentAcknowledgement } from '@/utils/session-payment';
import { readStartMarketHandoff } from '@/utils/start-properties-market';
import {
  CheckoutPane,
  useCheckout,
} from '@/nova/pages/configurator-v0-mx/steps/finish/components/payment';
import { resolvePaymentSurface } from '@/nova/pages/configurator-v0-mx/steps/finish/components/payment/payment-methods';
import type { CheckoutStoreApi } from '@/nova/pages/configurator-v0-mx/steps/finish/components/payment/store-api';
import { useConfigurator } from '../../../../store/configurator';
import { FINISH } from '../../finish.tokens';
import { PaymentCheckout } from './PaymentCheckout';
import { CheckoutError } from './CheckoutError';
import { useCheckoutEmail } from './useCheckoutEmail';
import { Root, Header, BackButton, BackLabel, HeaderTitle, Body } from './PaymentPane.styles';

/** What the completed checkout hands the step: whether money actually arrived (card charge
 *  cleared, or nothing was owed) and the backend's acknowledgement verdict when this pane
 *  made that call itself (SPEI / shared rail; null on the Stripe-only surface). */
export type PaymentOutcome = { settled: boolean; acknowledgement: PaymentAcknowledgement | null };

export type PaymentPaneProps = {
  email: string;
  amount: number;
  /** GROSS order total (before voucher) for conversion analytics. */
  grossAmount: number;
  /** Monthly recurring (product-features) total, for the Subscribe conversion. */
  monthlyAmount: number;
  currency: string;
  currencySymbol: string;
  onBack: () => void;
  onSuccess: (outcome: PaymentOutcome) => void;
};

const BackGlyph = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke={FINISH.backIconStroke} strokeWidth="1.5" />
    <path
      d="M13.5 8.5 10 12l3.5 3.5"
      stroke={FINISH.backIconStroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Desktop checkout pane. Resolves the payment surface once (`resolvePaymentSurface`), then renders
 * one of three subtrees, each a distinct component so the Stripe-only path never spins up the shared
 * `useCheckout` (which would create a SECOND invoice alongside `PaymentCheckout`'s own):
 *
 *  - `fallback: 'demo'` (a card region with no live Stripe key) renders the Stripe-only surface,
 *    whose `PaymentCheckout` falls back to the demo card checkout. `fallback: 'error'` (genuinely no
 *    rail) renders the local `CheckoutError`; it is defensive, as region rails are never empty today.
 *  - card only renders the unchanged Stripe-only `PaymentCheckout` look, no picker chrome.
 *  - otherwise (SPEI-only or a real choice) renders the shared cross-flow `CheckoutPane`, driven by
 *    one `useCheckout` instance built from this flow's own store callbacks.
 */
export const PaymentPane = (props: PaymentPaneProps): React.ReactElement => {
  const { currency, country, countryCode, recordCheckout, recordCardCheckout, setPaymentMethod, flushCheckout } =
    useConfigurator();
  // The marketing-site hand-off region, read once, is the operating-region fallback when the store
  // has not settled a `countryCode` yet.
  const handoff = React.useMemo(() => readStartMarketHandoff(), []);
  const railArgs = React.useMemo(
    () => ({ currency, or: countryCode ?? handoff.or, region: country }),
    [currency, countryCode, handoff.or, country],
  );
  const surface = React.useMemo(() => resolvePaymentSurface(railArgs), [railArgs]);

  // 'demo' = a card region with no live Stripe key: fall back to the demo card checkout (the
  // Stripe-only surface, whose `PaymentCheckout` renders `DemoCheckout`). 'error' is defensive:
  // region rails are never empty today, so a rail-less surface always resolves to 'demo'.
  if (surface.fallback === 'demo') return <StripePane {...props} />;
  if (surface.fallback === 'error') return <ErrorPane onBack={props.onBack} />;

  const { methods } = surface;
  if (methods.length === 1 && methods[0] === STRIPE_PROVIDER) return <StripePane {...props} />;

  const store: CheckoutStoreApi = { recordCheckout, recordCardCheckout, setPaymentMethod, flushCheckout };
  // Keyed by currency: the offered rails depend on it, so useCheckout's state must not outlive it.
  return <SharedPane key={currency} {...props} methods={methods} store={store} />;
};

/** No usable rail (a card region with no live Stripe key): the same chrome, a friendly error. */
function ErrorPane({ onBack }: { onBack: () => void }): React.ReactElement {
  return (
    <Root>
      <Header>
        <BackButton type="button" data-ga-id="cfg-finish-payment-back" onClick={onBack}>
          {BackGlyph}
          <BackLabel text="Back" tag="span" />
        </BackButton>
        <HeaderTitle text="Finalize payment" tag="span" />
      </Header>
      <Body>
        <CheckoutError />
      </Body>
    </Root>
  );
}

/**
 * The Stripe-only surface: the header shell, plus `PaymentCheckout` (the current live Stripe
 * Elements look). Unchanged from before the shared-checkout wiring; it owns its own email +
 * invoice and does NOT touch `useCheckout`.
 */
function StripePane({
  email: initialEmail,
  amount,
  grossAmount,
  monthlyAmount,
  currency,
  currencySymbol,
  onBack,
  onSuccess,
}: PaymentPaneProps): React.ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useCheckoutEmail(initialEmail);

  return (
    <Root>
      <Header>
        <BackButton type="button" data-ga-id="cfg-finish-payment-back" onClick={onBack}>
          {BackGlyph}
          <BackLabel text="Back" tag="span" />
        </BackButton>
        <HeaderTitle text="Finalize payment" tag="span" />
      </Header>

      <Body>
        <PaymentCheckout
          email={email}
          onEmail={setEmail}
          amount={amount}
          grossAmount={grossAmount}
          monthlyAmount={monthlyAmount}
          currency={currency}
          currencySymbol={currencySymbol}
          totalLabel={t('Order total')}
          payLabel={t('Pay for your brand')}
          onSuccess={() => onSuccess({ settled: true, acknowledgement: null })}
        />
      </Body>
    </Root>
  );
}

/**
 * The shared surface (SPEI-only or a card/SPEI choice). Hosts the single `useCheckout` instance
 * (the panes below it, `CheckoutPane` → `CheckoutBody`, are presentational) and renders the
 * cross-flow `CheckoutPane`, which brings its own back header (contextual on the SPEI rail).
 */
function SharedPane({
  email,
  amount,
  grossAmount,
  currency,
  currencySymbol,
  onBack,
  onSuccess,
  methods,
  store,
}: PaymentPaneProps & { methods: CheckoutProvider[]; store: CheckoutStoreApi }): React.ReactElement {
  const { t } = useTranslation();
  const checkout = useCheckout({ initialEmail: email, grossAmount, currency, methods, store });

  // Nothing left to collect (a voucher covered the whole bill, or the money was already in): show
  // the confirmation rather than asking for a payment that would be a second one. The step's own
  // acknowledge (via `onSuccess`) is exactly-once-guarded, so calling it here is safe.
  React.useEffect(() => {
    if (checkout.nothingToPay) onSuccess({ settled: true, acknowledgement: null });
  }, [checkout.nothingToPay, onSuccess]);

  // The payment is made: "I've made the transfer" on the bank rail, a cleared charge on the card
  // one. Register it with the backend (records the final funnel event + flushes the pending save
  // before the session advances), then hand off to the step's success screen. Settlement travels
  // up with the outcome: only the card rail's cleared charge counts here, so the SPEI buyer's
  // "made the transfer" advances the screen without counting a Purchase.
  const onPaid = React.useCallback(async (cardSettled: boolean): Promise<void> => {
    const result = await checkout.acknowledge();
    onSuccess({ settled: cardSettled, acknowledgement: result });
  }, [checkout, onSuccess]);

  return (
    <CheckoutPane
      checkout={checkout}
      amount={amount}
      currency={currency}
      currencySymbol={currencySymbol}
      totalLabel={t('Order total')}
      onBack={onBack}
      onPaid={(cardSettled) => void onPaid(cardSettled)}
    />
  );
}

export default PaymentPane;
